import i18n from '~lib/i18n';

import {
	weekOptions,
	weekdays,
	weekend,
	changeDateTimeToUTCTime,
	modifyDate,
	unsetTime,
} from './datetime';

/* * * * * * * * * * * * *
 * DEVELOPMENT UTILITIES *
 * * * * * * * * * * * * */
/**
 * Use typescript for this! E.g. value !== undefined or value != null
 * @deprecated
 */
export const isUndefined = (value): boolean => typeof value === 'undefined';
export const isNull = (value): boolean => !value && typeof value === 'object';
export const isNumber = (value): boolean => typeof value === 'number';
// No support for Number.isInteger by IE
// export const isInteger = (value) => (Number.isInteger(value));
export const isInteger = (value): boolean =>
	isNumber(value) && isFinite(value) && Math.floor(value) === value;
export const isBoolean = (value): boolean => typeof value === 'boolean';
export const isString = (value): boolean => typeof value === 'string';
export const isFullString = (value): boolean => isString(value) && !!value.trim().length;
export const isEmptyString = (value): boolean => isString(value) && !value.trim().length;
export const isArray = (value): boolean => typeof value === 'object' && Array.isArray(value);
export const isFullArray = (value): boolean => isArray(value) && !!value.length;
export const isEmptyArray = (value): boolean => isArray(value) && !value.length;
export const isObject = (value): boolean =>
	typeof value === 'object' && !isNull(value) && !isArray(value);
export const isEmptyObject = (value): boolean => isObject(value) && Object.keys(value).length === 0;
export const isFunction = (value): boolean => typeof value === 'function';

/**
 *
 * @deprecated Use src/utils/stringUtils
 */
export const capitalizeFirstCharacter = (value, othersLowercase = true) =>
	isFullString(value) ?
		`${value.slice(0, 1).toUpperCase()}${
			value.length > 1 ?
				othersLowercase ? value.slice(1).toLowerCase()
				:	value.slice(1)
			:	''
		}`
	:	'';

export const decimalBase = (value, currencySymbol) =>
	isNumber(value) && value >= 0 ? `${currencySymbol}${value.toFixed(2)}` : '-';

export const decimalAmount = (value, amount) =>
	isObject(amount) && isNumber(value) && amount.subtotal > 0 ?
		`${amount.currencySymbol}${value.toFixed(2)}`
	:	'-';

/* * * * * * * * * * * *
 * USER DATA UTILITIES *
 * * * * * * * * * * * */
export const getLanguageCode = () => localStorage.getItem('languageCode');

export const stringifyQueryParams = (params = {}) => {
	if (isObject(params)) {
		return Object.entries(params)
			.map((param, index) => `${!index ? '?' : '&'}${param[0]}=${param[1]}`)
			.join('');
	}

	return params;
};

export const ignoreKeyClick = (event, keyName) => {
	event.persist();
	if (event.key === keyName) {
		event.preventDefault();
	}
};

export const castMinutesToTimeString = (minutes, short = true) => {
	const days = Math.floor(minutes / 1440);
	const hours = Math.floor((minutes % 1440) / 60);
	const leftOverMinutes = minutes % 60;
	const dayString = days > 0 ? `${days}${i18n.t('ui.daysShort')}` : '';
	const hoursString = hours > 0 ? `${hours}${i18n.t('ui.hoursShort')}` : '';
	const leftOverMinutesString =
		leftOverMinutes > 0 ? `${leftOverMinutes}${i18n.t('ui.minutesShort')}` : '';
	const daysTime =
		days > 0 ? `${days} ${days === 1 ? i18n.t('ui.dayLong') : i18n.t('ui.daysLong')}` : '';
	const hoursTime = days > 0 && hours > 0 ? ' ' : '';
	const hoursDuration =
		hours > 0 ? `${hours} ${hours === 1 ? i18n.t('ui.hour') : i18n.t('ui.hoursDuration')}` : '';
	const leftOverMinutesTime =
		(hours > 0 && leftOverMinutes > 0) || (hours === 0 && days > 0 && leftOverMinutes > 0) ?
			' '
		:	'';
	const minutesTime =
		leftOverMinutes > 0 ?
			`${leftOverMinutes} ${leftOverMinutes === 1 ? i18n.t('ui.minute') : i18n.t('ui.minutes')}`
		:	'';
	return short ?
			`${dayString}${hoursString}${leftOverMinutesString}`
		:	`${daysTime}${hoursTime}${hoursDuration}${leftOverMinutesTime}${minutesTime}`;
};

export const createSummaryPeriod = (periodsArray, isSummary = false) => {
	const newPeriodArray = periodsArray.map((period) => {
		const dayStringsArray = period.days.split(', ');
		const days = dayStringsArray.map(
			(dayString) => weekOptions.filter((option) => option.value === dayString)[0]
		);

		const endDate = changeDateTimeToUTCTime(new Date(period.end));

		return {
			...period,
			days,
			start: changeDateTimeToUTCTime(new Date(period.start)),
			end:
				endDate.getHours() === 23 && endDate.getMinutes() === 59 && endDate.getSeconds() === 59 ?
					modifyDate(unsetTime(endDate), { date: '+1' })
				:	endDate,
		};
	});

	return isSummary ? newPeriodArray : createEditableDaysArrayInPricingModel(newPeriodArray);
};

const createEditableDaysArrayInPricingModel = (pricingModel) => {
	const newPricingModel = pricingModel.map((entry) => {
		return {
			...entry,
			days: entry.days.reduce((acc, cur) => {
				return [
					...acc,
					...(cur.value === 'weekdays' ? weekdays : []),
					...(cur.value === 'weekend' ? weekend : []),
					...(cur.value === 'allWeek' ? [...weekdays, ...weekend] : []),
					...(cur.value !== 'weekdays' && cur.value !== 'weekend' && cur.value !== 'allWeek' ?
						[cur]
					:	[]),
				];
			}, []),
		};
	});

	return newPricingModel;
};

export const timeConvert = (value) => ({
	minutes: value % 60,
	hours: Math.floor(value / 60) % 24,
	day: Math.floor(value / (60 * 24)),
});

export const instanceName = (value) => {
	const category = value.categoryReference.type;
	if (category === 'cars') {
		return value.itemCar.licensePlate;
	} else if (category === 'bikes') {
		return value.itemBike.bikeNumber;
	} else if (category === 'offices') {
		return value.itemOffice.roomNumber;
	} else if (category === 'trailers') {
		return value.itemTrailer.trailerNumber;
	} else if (category === 'parkingLots') {
		return value.itemParkingLot.parkingNumber;
	} else if (category === 'assets') {
		return value.itemAsset.assetNumber;
	} else if (category === 'boats') {
		return value.name;
	} else {
		return '-';
	}
};

export const instanceLabel = (value) => {
	if (value === 'cars') {
		return i18n.t('ui.label.licensePlate');
	} else if (value === 'bikes') {
		return i18n.t('ui.label.bikeNumber');
	} else if (value === 'offices') {
		return i18n.t('ui.label.roomNumberInstance');
	} else if (value === 'trailers') {
		return i18n.t('ui.label.trailerNumber');
	} else if (value === 'parkingLots') {
		return i18n.t('ui.label.parking.parkingNumber');
	} else if (value === 'assets') {
		return i18n.t('ui.label.assetNumber.placeholder');
	} else if (value === 'boats') {
		return i18n.t('ui.label.registrationNumber');
	} else {
		return '-';
	}
};

const deviceType = {
	none: 'ui.label.device.none',
	bikeLock: 'ui.label.bikeLock',
	doorLock: 'ui.label.doorLock',
	carLock: 'ui.label.carLock',
	bmwCar: 'BMW',
	trailerLock: 'ui.label.trailerLock',
	batteryBackupSystem: 'ui.label.batteryBackupSystem',
	borderRouter: 'ui.label.borderRouter',
	vehicleTracker: 'ui.label.vehicleTracker',
	vehicleTrackerCan: 'ui.label.vehicleTrackerCan',
	vehicleTrackerObd: 'ui.label.vehicleTrackerOBD',
	vehicleTrackerPro: 'ui.label.vehicleTrackerPro',
	cpacBoat: 'ui.label.cpacBoat',
	vok: 'ui.label.vok',
};

export const getDeviceTypeLabel = (
	value:
		| 'none'
		| 'bikeLock'
		| 'doorLock'
		| 'carLock'
		| 'bmwCar'
		| 'trailerLock'
		| 'batteryBackupSystem'
		| 'borderRouter'
		| 'vehicleTracker'
		| 'vehicleTrackerCan'
		| 'vehicleTrackerObd'
		| 'vehicleTrackerPro'
		| 'cpacBoat'
		| 'vok'
		| undefined
): string | null => {
	if (value === undefined) return null;
	const deviceTypeLabel = i18n.t(deviceType[value]);
	if (deviceTypeLabel === undefined) return null;
	return isFullString(value) ? deviceTypeLabel : null;
};

export const getCommunicationTypeLabel = (
	value:
		| 'None'
		| 'none'
		| 'totp'
		| 'rfid'
		| 'ble'
		| 'lte'
		| 'thread'
		| 'offline'
		| 'server'
		| undefined
): string | null => {
	const unlockType = {
		None: 'None',
		none: 'None',
		totp: 'Keycode',
		rfid: 'NFC',
		ble: 'BLE',
		lte: '4G',
		thread: 'Thread',
		offline: 'Offline',
		server: 'Server',
	};
	if (value === undefined) return null;
	return unlockType[value];
};

export const handleHubReference = (value, isFirst = false) => {
	const getValid = (value, isFirst = false) =>
		!isUndefined(value) ? `${isFirst ? '' : ' '}${value}` : '';
	if (isObject(value) && isObject(value.address)) {
		const { street, number, city, numberAddition } = value.address;
		if (isObject(value.address)) {
			return `${getValid(street, isFirst)}${getValid(number)}${getValid(numberAddition)},${getValid(city)}`;
		}
	} else {
		return '-';
	}
};

export const handleCultureCode = (value) => {
	const shortCode = value.slice(0, 2);
	if (shortCode === 'nl') {
		return i18n.t('ui.dutch');
	} else if (shortCode === 'de') {
		return i18n.t('ui.german');
	} else if (shortCode === 'en') {
		return i18n.t('ui.english');
	}
};

/**
 * Generates an array of finance table headers based on the specified parameters.
 *
 * @param {boolean} [hidePeriod=false] - Whether to hide the 'period' header.
 * @param {boolean} [hideOpen=false] - Whether to hide the 'open' header.
 * @param {boolean} [hideStatus=false] - Whether to hide the 'status' header.
 * @returns {Array} An array of finance table headers.
 */

export const getFinanceTableHeaders = (
	hidePeriod = false,
	hideOpen = false,
	hideStatus = false
) => [
	...(hidePeriod ? [] : [{ name: 'period', content: i18n.t('ui.period') }]),
	{ name: 'subtotal', content: i18n.t('ui.label.finance.subTotal') },
	{ name: 'discount', content: i18n.t('ui.discount') },
	{ name: 'totaExclVat', content: i18n.t('ui.label.finance.totaExclVat') },
	{ name: 'vat', content: i18n.t('ui.label.finance.Vat') },
	{ name: 'refund', content: i18n.t('ui.refund') },
	{ name: 'totalInclVat', content: i18n.t('ui.label.finance.totalInclVat') },
	...(hideOpen ? [] : [{ name: 'open', content: i18n.t('ui.open') }]),
	...(hideStatus ? [] : [{ name: 'status', content: i18n.t('ui.label.status') }]),
];

/**
 * Map the battery level enum to translation keys
 * TODO: eventually this should happen in the respective service layer
 * @param level
 * @returns
 */
export const mapBatteryLevelEnum = (level: 'high' | 'medium' | 'low' | 'critical') => {
	const translationKey = (() => {
		switch (level) {
			case 'high':
				return 'full';
			case 'medium':
				return 'ok';
			default:
				return level;
		}
	})();

	return i18n.t(`ui.label.${translationKey}`);
};
